import React, { useState } from 'react';
import { XMarkIcon, FlagIcon } from '@heroicons/react/24/outline';
import Text from '../common/Text';
import Button from '../common/Button';

export type FlagReason =
  | 'incorrect_answer'
  | 'unclear_question'
  | 'grammatical_error'
  | 'wrong_difficulty'
  | 'other';

interface FlagQuestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFlag: (reason: FlagReason, description?: string) => Promise<void>;
  questionText?: string;
}

const FLAG_REASONS: { value: FlagReason; label: string; description: string }[] = [
  {
    value: 'incorrect_answer',
    label: 'Incorrect Answer',
    description: 'The marked correct answer is wrong'
  },
  {
    value: 'unclear_question',
    label: 'Unclear Question',
    description: 'The question is confusing or ambiguous'
  },
  {
    value: 'grammatical_error',
    label: 'Grammar/Spelling Error',
    description: 'There are grammatical or spelling mistakes'
  },
  {
    value: 'wrong_difficulty',
    label: 'Wrong Difficulty Level',
    description: 'The difficulty doesn\'t match the question'
  },
  {
    value: 'other',
    label: 'Other Issue',
    description: 'Something else is wrong with this question'
  }
];

const FlagQuestionModal: React.FC<FlagQuestionModalProps> = ({
  isOpen,
  onClose,
  onFlag,
  questionText
}) => {
  const [selectedReason, setSelectedReason] = useState<FlagReason | null>(null);
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!selectedReason) return;

    setIsSubmitting(true);
    try {
      await onFlag(selectedReason, description.trim() || undefined);
      // Reset form
      setSelectedReason(null);
      setDescription('');
      onClose();
    } catch (error) {
      console.error('Failed to flag question:', error);
      // TODO: Show error toast
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedReason(null);
      setDescription('');
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={handleClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <FlagIcon className="w-6 h-6 text-red-500" />
            <Text variant="h5" className="!font-semibold">Report Question</Text>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {questionText && (
            <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
              <Text variant="p" className="text-xs text-gray-600 dark:text-gray-400 mb-1.5">
                Question:
              </Text>
              <Text variant="p" className="text-sm text-gray-900 dark:text-gray-100">
                {questionText}
              </Text>
            </div>
          )}

          <div>
            <Text variant="h4" className="mb-3">
              What's wrong with this question?
            </Text>
            <div className="space-y-2">
              {FLAG_REASONS.map((reason) => (
                <button
                  key={reason.value}
                  onClick={() => setSelectedReason(reason.value)}
                  disabled={isSubmitting}
                  className={`w-full p-3 text-left rounded-lg border transition-all ${
                    selectedReason === reason.value
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-500 ring-opacity-50'
                      : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        selectedReason === reason.value
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                    >
                      {selectedReason === reason.value && (
                        <div className="w-1.5 h-1.5 rounded-full bg-white" />
                      )}
                    </div>
                    <div className="flex-1">
                      <Text variant="p" className="font-medium text-sm text-gray-900 dark:text-gray-100">
                        {reason.label}
                      </Text>
                      <Text variant="p" className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                        {reason.description}
                      </Text>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {selectedReason && (
            <div>
              <Text variant="p" className="text-sm font-medium mb-2">
                Additional Details (Optional)
              </Text>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Provide more details about the issue..."
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 resize-none"
                rows={3}
                disabled={isSubmitting}
              />
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              onClick={handleClose}
              variant="secondary"
              size="md"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="primary"
              size="md"
              disabled={!selectedReason || isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Report'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlagQuestionModal;

